import { Environment } from 'phoenix/constants';
import { getSimulatedEnvironment } from 'phoenix/constants/Support';
import { ErrorLogger } from 'phoenix/util/ErrorLogger';

interface SalesforceEmbeddedServiceUtilAPI {
    launchChat: () => Promise<void>;
    getEmbeddedMessagingFrame?: () => HTMLIFrameElement | null;
    showChatButton?: () => boolean;
    hideChatButton?: () => boolean;
}

interface SalesforceEmbeddedServiceBootstrap {
    settings: {
        language?: string;
        omitSandbox?: boolean;
        [key: string]: unknown;
    };
    prechatAPI: {
        setHiddenPrechatFields: (fields: Record<string, string>) => void;
    };
    utilAPI?: SalesforceEmbeddedServiceUtilAPI;
    init: (orgId: string, eswConfigDevName: string, siteURL: string, snippetConfig: { scrt2URL: string }) => void;
}

declare global {
    interface Window {
        embeddedservice_bootstrap?: SalesforceEmbeddedServiceBootstrap;
    }
}

export interface InitSalesforceOptions {
    locale: string;
}


interface SalesforceConfig {
    eswConfigDevName: string;
    siteURL: string;
    bootstrapURL: string;
}

type ConfigVariant = 'pro' | 'retail';

const configs: Record<ConfigVariant, SalesforceConfig> = {
    pro: {
        eswConfigDevName: 'StoneX_One_Pro_No_Auth',
        siteURL: 'https://gaincapital.my.site.com/ESWStoneXOneProNoAuth1737474513193',
        bootstrapURL: 'https://gaincapital.my.site.com/ESWStoneXOneProNoAuth1737474513193/assets/js/bootstrap.min.js'
    },
    retail: {
        eswConfigDevName: 'StoneX_One_No_Auth',
        siteURL: 'https://gaincapital.my.site.com/ESWStoneXOneNoAuth1737114800730',
        bootstrapURL: 'https://gaincapital.my.site.com/ESWStoneXOneNoAuth1737114800730/assets/js/bootstrap.min.js'
    }
}

function getConfigFromEnv(env: Environment): SalesforceConfig {
    return (env === 'pro') ? configs.pro : configs.retail;
}

export default function InitSalesforce({ locale }: InitSalesforceOptions): void {
    if (window.embeddedservice_bootstrap) {
        initEmbeddedMessaging(locale);
    } else {
        const env = getSimulatedEnvironment();
        const { bootstrapURL } = getConfigFromEnv(env);

        const s = document.createElement('script');
        s.setAttribute('src', bootstrapURL);
        s.onload = function () {
            if (!window.embeddedservice_bootstrap) {
                const error = new Error('Salesforce embeddedservice_bootstrap was not loaded properly');
                ErrorLogger.RecordError(error, "Salesforce Chat - Couldn't Load Script");
                return;
            }
            initEmbeddedMessaging(locale);
        };
        document.body.appendChild(s);
    }
}

function initEmbeddedMessaging(locale?: string) {
    const bootstrap = window.embeddedservice_bootstrap;
    if (!bootstrap) {
        throw new Error('Salesforce embeddedservice_bootstrap is not loaded');
    }

    try {
        bootstrap.settings.language = locale || 'en_US';
        bootstrap.settings.omitSandbox = true;
        window.addEventListener('onEmbeddedMessagingReady', () => {
            bootstrap.prechatAPI.setHiddenPrechatFields({
                Preauth_Name: '',
                Preauth_Email: '',
                Language: 'en_US',
                Button_Name: 'StoneX_One'
            });
        });

        const env = getSimulatedEnvironment();
        const { eswConfigDevName, siteURL } = getConfigFromEnv(env);

        bootstrap.init('00D300000000CHg', eswConfigDevName, siteURL, {
            scrt2URL: 'https://gaincapital.my.salesforce-scrt.com'
        });
        console.log('After init, utilAPI exists?', !!bootstrap.utilAPI);
    } catch (err) {
        console.error('Error loading Embedded Messaging: ', err);
    }
}

export function OpenSalesforceChatWindow(): void {
    if (window.embeddedservice_bootstrap && window.embeddedservice_bootstrap?.utilAPI) {
        window.embeddedservice_bootstrap.utilAPI.launchChat().catch((error) => {
            console.error('Failed to launch chat window:', error);
        });
    } else {
        console.error('EmbeddedServiceBootstrap API is not available.');
    }
}
